import { useContext, useState } from "react";
import CloseIcon from "../../src/assets/img/closeIcon.svg";
import useTranslator from "../hooks/useTranslator";
import LandLordContext from "../context/landlord/LandLordContext";
import { toast } from "../helper/common";

const SendTenantEmailDrawer = ({
  user,
  isOpen,
  onClose,
  globalTenantDocuments,
  globalAllForensicDocuments,
  currentLang,
  globalTenantDocsHistory,
  globalMandatoryDocsList,
}) => {
  const { _t } = useTranslator();
  const { sendNoteToTenant } = useContext(LandLordContext);
  const [sendNoteLoading, setSendNoteLoading] = useState(false);
  const [selectedSections, setSelectedSections] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [isDocumentSelected, setIsDocumentSelected] = useState(false);
  const [note, setNote] = useState("");

  // toggle sections like KYC, Financial Score, Guarantor
  const toggleSection = (value, key) => {
    if (key === "documents") {
      setIsDocumentSelected((prev) => !prev);
      // clear docs if unchecked
      if (isDocumentSelected) {
        setSelectedDocuments([]);
      }
    } else {
      setSelectedSections((prev) =>
        prev.includes(value)
          ? prev.filter((item) => item !== value)
          : [...prev, value]
      );
    }
  };

  // toggle individual documents
  const toggleDocument = (docName) => {
    setSelectedDocuments((prev) =>
      prev.includes(docName)
        ? prev.filter((item) => item !== docName)
        : [...prev, docName]
    );
  };

  const handleSendNote = async () => {
    if (!note) {
      toast.error(_t("pleaseEnterANote"));
      return;
    }
    try {
      setSendNoteLoading(true);

      const finalSelectedItems = [
        ...selectedSections,
        ...(isDocumentSelected ? selectedDocuments : []),
      ];

      const data = {
        tenant_id: user?._id,
        selectedItems: finalSelectedItems,
        note,
      };

      await sendNoteToTenant(data);
      toast.success(
        _t(
          "noteSentSuccessfully",
          "Text used when landlord send mail to tenant"
        )
      );
      onClose();
    } catch (error) {
      toast.error(
        _t("FailedToSendNote", "Text used when landlord send mail to tenant")
      );
    } finally {
      setSendNoteLoading(false);
    }
  };

  return (
    <div>
      <div className="wrapper-modal" onClick={onClose}>
        <div
          className={`doc-history-popup send-email-drawer total-salary-modal ttt ${
            isOpen ? "open" : ""
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <span className="drawerIcon" onClick={onClose} />
          <div>
            <div className="doc-history-header">
              <img
                src={CloseIcon}
                alt=""
                className="send-email-close-btn"
                onClick={onClose}
              />
              <h4 className="custom-modal-heading">
                {_t("sendTenantEmailFor")}
              </h4>
            </div>
            <div className="send-email-content">
              {/* Sections */}
              <div className="send-email-checkbox-container">
                <div className="send-email-section">
                  {[
                    {
                      label: _t(
                        "kycIdentification",
                        "Text used in send note popup"
                      ),
                    },
                    {
                      label: _t(
                        "financialScore",
                        "Text used in send note popup"
                      ),
                    },
                    { key: "documents", label: _t("documents") },
                    { label: _t("guarantor") },
                  ].map(({ key, label }, index) => (
                    <label key={index} className="send-email-checkbox">
                      <input
                        type="checkbox"
                        checked={
                          key === "documents"
                            ? isDocumentSelected
                            : selectedSections.includes(label)
                        }
                        onChange={() => toggleSection(label, key)}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>

                {/* Documents */}
                {isDocumentSelected && (
                  <div>
                    <h4 className="custom-modal-heading custom-modal-heading2">
                      {_t("documents")}
                    </h4>
                    <div className="send-email-section">
                      {Object.entries(globalMandatoryDocsList)?.map(
                        ([key, value]) => {
                          const isPayslipKey = key
                            .toLowerCase()
                            .includes("payslip");

                          const payslipDocsCount = Object.keys(
                            globalTenantDocsHistory
                          ).filter((docKey) =>
                            docKey.toLowerCase().includes("payslip")
                          ).length;

                          if (isPayslipKey) {
                            const payslipOrder = [
                              "1st month payslip",
                              "2nd month payslip",
                              "3rd month payslip",
                            ];
                            const index = payslipOrder.indexOf(key);

                            if (index < payslipDocsCount) {
                              return null;
                            }
                          }

                          // normal missing document check
                          if (
                            !(key in globalTenantDocsHistory && !isPayslipKey)
                          ) {
                            const docName = _t(key, "", "fixed");
                            return (
                              <label key={key} className="send-email-checkbox">
                                <input
                                  type="checkbox"
                                  checked={selectedDocuments.includes(docName)}
                                  onChange={() => toggleDocument(docName)}
                                />
                                <span>{docName}</span>
                                <span className="missing-label">
                                  <img
                                    src="/images/missing-doc-icon.svg"
                                    alt=""
                                  />
                                  {_t("missing")}
                                </span>
                              </label>
                            );
                          }

                          return null;
                        }
                      )}
                      {[
                        ...new Set(
                          globalTenantDocuments.map((doc) => doc?.type)
                        ),
                      ].map((type, index) => {
                        const doc = globalTenantDocuments.find(
                          (d) => d.type === type
                        );

                        const docName =
                          globalAllForensicDocuments?.[doc?.file_name]
                            ?.documentNameTranslated?.[
                            currentLang === "en-us" ? "en" : "es"
                          ] ?? _t(doc?.type, "", "fixed");

                        return (
                          <label key={index} className="send-email-checkbox">
                            <input
                              type="checkbox"
                              checked={selectedDocuments.includes(docName)}
                              onChange={() => toggleDocument(docName)}
                            />
                            <span>{docName}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Note */}
              <div className="forminput customInput">
                <label className="forminput label-text">
                  <h6 className="modal-label">{_t("note")}</h6>
                </label>
                <textarea
                  className="form-control send-email-note modal-input"
                  placeholder="Type Here..."
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="addGuarantorButtonWrapper d-flex align-items-center justify-content-center">
            <button
              className="btnNext addGuarantorButton send-email-btn d-flex align-items-center justify-content-center"
              onClick={handleSendNote}
              disabled={sendNoteLoading}
            >
              {_t("sendViaEmail")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendTenantEmailDrawer;
